import { Fragment } from 'react';
import type { MessageEntity, MessageEntityType } from '../types';

/**
 * Расширенные типы для парсинга из текста (fallback)
 */
type ParsedEntityType = 'url_parsed' | 'email_parsed' | 'mention_parsed' | 'hashtag_parsed';

/**
 * Тип сущности в тексте сообщения (для внутреннего использования)
 */
interface TextEntity {
  type: MessageEntityType | ParsedEntityType;
  start: number;
  end: number;
  text: string;
  url?: string;
  language?: string;
}

/**
 * Регулярные выражения для поиска сущностей в тексте (fallback)
 */
const PATTERNS = {
  // URL (http/https)
  url: /https?:\/\/[^\s<>"[\]{}|\\^`]+/gi,
  // Email
  email: /[\w.+-]+@[\w-]+\.[\w.-]+/gi,
  // Упоминание (@username)
  mention: /@[\w_]{3,32}/gi,
  // Хэштег
  hashtag: /#[\wа-яёА-ЯЁ]+/gi,
};

/**
 * Конвертировать MessageEntity из Telegram API в TextEntity для рендеринга
 */
function convertTelegramEntities(text: string, entities: MessageEntity[]): TextEntity[] {
  return entities.map(entity => {
    const start = entity.offset;
    const end = entity.offset + entity.length;
    const entityText = text.slice(start, end);

    return {
      type: entity.type,
      start,
      end,
      text: entityText,
      url: entity.url || (entity.type === 'url' ? entityText : undefined),
      language: entity.language,
    };
  }).sort((a, b) => a.start - b.start);
}

/**
 * Найти сущности в тексте (fallback, если entities не предоставлены)
 */
function findEntitiesFromText(text: string): TextEntity[] {
  const entities: TextEntity[] = [];

  // URLs
  let match: RegExpExecArray | null;
  while ((match = PATTERNS.url.exec(text)) !== null) {
    entities.push({
      type: 'url_parsed',
      start: match.index,
      end: match.index + match[0].length,
      text: match[0],
      url: match[0],
    });
  }

  // Emails
  PATTERNS.email.lastIndex = 0;
  while ((match = PATTERNS.email.exec(text)) !== null) {
    // Проверяем, что email не внутри URL
    const isInsideUrl = entities.some(
      e => e.type === 'url_parsed' && match!.index >= e.start && match!.index + match![0].length <= e.end
    );
    if (!isInsideUrl) {
      entities.push({
        type: 'email_parsed',
        start: match.index,
        end: match.index + match[0].length,
        text: match[0],
        url: `mailto:${match[0]}`,
      });
    }
  }

  // Mentions
  PATTERNS.mention.lastIndex = 0;
  while ((match = PATTERNS.mention.exec(text)) !== null) {
    entities.push({
      type: 'mention_parsed',
      start: match.index,
      end: match.index + match[0].length,
      text: match[0],
      url: `https://t.me/${match[0].slice(1)}`,
    });
  }

  // Hashtags
  PATTERNS.hashtag.lastIndex = 0;
  while ((match = PATTERNS.hashtag.exec(text)) !== null) {
    entities.push({
      type: 'hashtag_parsed',
      start: match.index,
      end: match.index + match[0].length,
      text: match[0],
    });
  }

  // Сортируем по позиции начала
  return entities.sort((a, b) => a.start - b.start);
}

/**
 * Удалить перекрывающиеся сущности
 */
function removeOverlapping(entities: TextEntity[]): TextEntity[] {
  const result: TextEntity[] = [];
  let lastEnd = 0;

  for (const entity of entities) {
    if (entity.start >= lastEnd) {
      result.push(entity);
      lastEnd = entity.end;
    }
  }

  return result;
}

interface RenderMessageTextProps {
  text: string;
  /** JSON строка или массив MessageEntity из Telegram API */
  entities?: string | MessageEntity[] | null;
  className?: string;
  isOutgoing?: boolean;
}

/**
 * Компонент для рендеринга форматированного текста сообщения
 * Поддерживает: bold, italic, underline, strikethrough, code, pre, URLs, emails, mentions, hashtags и др.
 */
export function RenderMessageText({ text, entities: entitiesProp, className, isOutgoing }: RenderMessageTextProps) {
  // Парсим entities если это JSON строка
  let parsedEntities: MessageEntity[] | null = null;
  if (entitiesProp) {
    if (typeof entitiesProp === 'string') {
      try {
        parsedEntities = JSON.parse(entitiesProp);
      } catch {
        parsedEntities = null;
      }
    } else {
      parsedEntities = entitiesProp;
    }
  }

  // Используем entities из Telegram API или fallback на парсинг текста
  const entities = parsedEntities && parsedEntities.length > 0
    ? removeOverlapping(convertTelegramEntities(text, parsedEntities))
    : removeOverlapping(findEntitiesFromText(text));

  if (entities.length === 0) {
    return <span className={className}>{text}</span>;
  }

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  entities.forEach((entity, index) => {
    // Добавляем текст перед сущностью
    if (entity.start > lastIndex) {
      parts.push(
        <Fragment key={`text-${index}`}>
          {text.slice(lastIndex, entity.start)}
        </Fragment>
      );
    }

    // Рендерим сущность
    const entityNode = renderEntity(entity, isOutgoing);
    parts.push(<Fragment key={`entity-${index}`}>{entityNode}</Fragment>);

    lastIndex = entity.end;
  });

  // Добавляем оставшийся текст
  if (lastIndex < text.length) {
    parts.push(
      <Fragment key="text-end">
        {text.slice(lastIndex)}
      </Fragment>
    );
  }

  return <span className={className}>{parts}</span>;
}

/**
 * Рендеринг отдельной сущности
 */
function renderEntity(entity: TextEntity, isOutgoing?: boolean): React.ReactNode {
  const linkClassName = isOutgoing
    ? 'text-primary-foreground/90 underline underline-offset-2 hover:text-primary-foreground'
    : 'text-primary underline underline-offset-2 hover:text-primary/80';

  const highlightClassName = isOutgoing
    ? 'text-primary-foreground/90'
    : 'text-primary';

  switch (entity.type) {
    // Форматирование текста
    case 'bold':
      return <strong>{entity.text}</strong>;

    case 'italic':
      return <em>{entity.text}</em>;

    case 'underline':
      return <u>{entity.text}</u>;

    case 'strikethrough':
      return <s>{entity.text}</s>;

    case 'code':
      return (
        <code className="px-1 py-0.5 bg-black/10 dark:bg-white/10 rounded text-[0.9em] font-mono">
          {entity.text}
        </code>
      );

    case 'pre':
      return (
        <pre className="px-2 py-1 my-1 bg-black/10 dark:bg-white/10 rounded text-[0.9em] font-mono whitespace-pre-wrap overflow-x-auto">
          {entity.language && (
            <div className="text-[0.75em] text-muted-foreground mb-1">{entity.language}</div>
          )}
          {entity.text}
        </pre>
      );

    case 'spoiler':
      return (
        <span className="bg-muted text-muted hover:bg-transparent hover:text-inherit transition-all cursor-pointer rounded px-0.5">
          {entity.text}
        </span>
      );

    case 'blockquote':
      return (
        <blockquote className="border-l-2 border-muted-foreground/30 pl-2 my-1 italic text-muted-foreground">
          {entity.text}
        </blockquote>
      );

    // Ссылки
    case 'url':
    case 'url_parsed':
    case 'textUrl':
    case 'text_link':
      return (
        <a
          href={entity.url || entity.text}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClassName}
          onClick={(e) => e.stopPropagation()}
        >
          {entity.text}
        </a>
      );

    case 'email':
    case 'email_parsed':
      return (
        <a
          href={entity.url || `mailto:${entity.text}`}
          className={linkClassName}
          onClick={(e) => e.stopPropagation()}
        >
          {entity.text}
        </a>
      );

    case 'phone':
    case 'phone_number':
      return (
        <a
          href={`tel:${entity.text.replaceAll(/[^\d+]/g, '')}`}
          className={linkClassName}
          onClick={(e) => e.stopPropagation()}
        >
          {entity.text}
        </a>
      );

    case 'mention':
    case 'mention_parsed':
      return (
        <a
          href={entity.url || `https://t.me/${entity.text.slice(1)}`}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClassName}
          onClick={(e) => e.stopPropagation()}
        >
          {entity.text}
        </a>
      );

    case 'hashtag':
    case 'hashtag_parsed':
      return (
        <span className={highlightClassName}>
          {entity.text}
        </span>
      );

    case 'cashtag':
      return (
        <span className={highlightClassName}>
          {entity.text}
        </span>
      );

    case 'botCommand':
    case 'bot_command':
      return (
        <span className={`${highlightClassName} font-medium`}>
          {entity.text}
        </span>
      );

    case 'customEmoji':
    case 'custom_emoji':
      // Custom emoji - показываем текст как есть (эмодзи)
      return <span>{entity.text}</span>;

    case 'mention_name':
      // Упоминание с конкретным userId
      return (
        <span className={linkClassName} style={{ cursor: 'pointer' }}>
          {entity.text}
        </span>
      );

    default:
      return entity.text;
  }
}
